import React from "react";
import { Link, useParams } from "react-router-dom";
import NotFound from "./NotFound";
import { ArrowRight, ArrowLeft, Phone } from "lucide-react";
import { HubHeader, HubFooter } from "../components/HubChrome";
import { useSeo } from "../lib/seo";
import { blog } from "../seo/schema";
import { PostCard } from "./Blog";
import { ShareButtons } from "../components/ShareButtons";

const BOOK_LABEL = { "/escape": "The Escape", "/europe": "The Europe Experience", "/getaway": "The Getaway" };

const Block = ({ b }) => {
  if (b.type === "h2") return <h2 className="font-cond font-bold uppercase tracking-wide text-white text-2xl sm:text-3xl mt-12 mb-4">{b.text}</h2>;
  if (b.type === "ul")
    return (
      <ul className="my-6 space-y-3">
        {b.items.map((it) => (
          <li key={it} className="flex gap-3 text-white/80 leading-relaxed">
            <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#E50914] shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  return <p className="my-5 text-white/80 text-base sm:text-[17px] leading-relaxed">{b.text}</p>;
};

const CtaText = ({ text, to }) => {
  const [before, after] = text.split("HouseHeadsAtSea.com");
  return (
    <p className="text-white text-lg leading-relaxed">
      {before}
      <Link to={to} data-testid="blog-cta-inline-link" className="text-[#E50914] underline underline-offset-4 hover:text-white transition-colors">HouseHeadsAtSea.com</Link>
      {after}
    </p>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = blog.posts.find((p) => p.slug === slug);
  useSeo(`/blog/${slug}`);
  if (!post) return <NotFound backTo="/blog" backLabel="Back to the blog" />;
  const sailings = blog.posts.filter((p) => p.booking && !p.guide);
  const others = blog.posts.filter((p) => p.slug !== slug).slice(0, 3);
  const related = post.related && blog.posts.find((p) => p.slug === post.related);

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="blog-post-page">
      <HubHeader />
      <article className="max-w-3xl mx-auto px-5 pt-12 sm:pt-16 pb-16">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 font-cond uppercase tracking-widest text-xs text-white/50 hover:text-white transition-colors">
          <ArrowLeft size={14} /> All posts
        </Link>
        <p className="mt-8 font-cond uppercase tracking-[0.4em] text-[#E50914] text-sm">{post.kicker}</p>
        <h1 data-testid="blog-post-h1" className="mt-3 font-display uppercase text-4xl sm:text-5xl leading-[1.02]">{post.title}</h1>
        <p className="mt-6 text-white text-lg sm:text-xl leading-relaxed">{post.lede}</p>
        <div className="mt-8">
          <ShareButtons slug={post.slug} title={post.title} testPrefix="blog-top" />
        </div>
      </article>

      <figure className="max-w-5xl mx-auto px-5">
        <img data-testid="blog-post-cover" src={post.cover} alt={post.coverAlt} className="w-full aspect-[16/9] object-cover rounded-2xl" />
        <figcaption className="mt-3 text-white/40 text-xs">{post.coverCaption}</figcaption>
      </figure>

      <div className="max-w-3xl mx-auto px-5 pb-8" data-testid="blog-post-body">
        {post.blocks.map((b, i) => <Block key={i} b={b} />)}

        <div className="mt-10 pt-8 border-t border-white/10">
          <ShareButtons slug={post.slug} title={post.title} testPrefix="blog-bottom" />
        </div>

        {related && (
          <Link
            to={`/blog/${related.slug}`}
            data-testid="blog-related-link"
            className="mt-10 flex items-center justify-between gap-4 bg-[#111111] border border-white/10 hover:border-[#E50914]/60 rounded-xl px-5 py-4 transition-colors"
          >
            <span>
              <span className="block font-cond uppercase tracking-widest text-[10px] text-[#E50914]">Read next</span>
              <span className="block text-white/85 text-sm sm:text-base mt-1">{related.title}</span>
            </span>
            <ArrowRight size={16} className="text-[#E50914] shrink-0" />
          </Link>
        )}

        <div className="mt-12 bg-[#111111] border border-[#E50914]/40 rounded-2xl p-6 sm:p-8" data-testid="blog-post-cta">
          <CtaText text={post.cta} to={post.booking || "/"} />
          <div className="mt-6 flex flex-wrap gap-3">
            {post.booking && (
              <Link to={post.booking} data-testid="blog-book-btn" className="inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#c20710] rounded-full px-6 py-3 font-cond uppercase tracking-widest text-sm transition-colors">
                Book {BOOK_LABEL[post.booking]} <ArrowRight size={16} />
              </Link>
            )}
            <a href="tel:+14706328963" data-testid="blog-call-btn" className="inline-flex items-center gap-2 border border-white/20 hover:border-white rounded-full px-6 py-3 font-cond uppercase tracking-widest text-sm transition-colors">
              <Phone size={16} /> 470-632-8963
            </a>
          </div>
        </div>

        {post.pickSailing && (
          <section className="mt-12" data-testid="blog-sailing-links">
            <h2 className="font-cond font-bold uppercase tracking-wide text-white text-2xl mb-4">Pick your sailing</h2>
            <div className="space-y-3">
              {sailings.map((s) => (
                <Link key={s.slug} to={`/blog/${s.slug}`} data-testid={`blog-sailing-link-${s.slug}`} className="flex items-center justify-between gap-4 bg-[#111111] border border-white/10 hover:border-[#E50914]/60 rounded-xl px-5 py-4 transition-colors">
                  <span className="text-white/85 text-sm sm:text-base">{s.title}</span>
                  <ArrowRight size={16} className="text-[#E50914] shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <section className="border-t border-white/10 mt-8">
        <div className="max-w-7xl mx-auto px-5 py-16">
          <p className="font-cond uppercase tracking-[0.4em] text-white/50 text-sm mb-6">More from the blog</p>
          <div className="grid md:grid-cols-3 gap-6">
            {others.map((p, i) => <PostCard key={p.slug} post={p} i={i} />)}
          </div>
        </div>
      </section>
      <HubFooter />
    </div>
  );
}
